import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AOS from "aos";
import "aos/dist/aos.css";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

export default function Profile({ user, setUser }) {
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedData, setEditedData] = useState({ name: "", profile_pic: "" });

  // Charger les données du profil
  useEffect(() => {
    const fetchData = async () => {
      if (!user?.id) {
        console.log("Aucun utilisateur chargé, en attente...");
        setIsLoaded(true);
        return;
      }
      try {
        const response = await axios.get(`${API_BASE_URL}/users/${user.id}`, {
          headers: { Authorization: `Bearer ${user.access_token}` },
        });
        console.log("Données du profil:", response.data);
        setProfileData(response.data);
        setEditedData({
          name: response.data.name || "",
          profile_pic: response.data.profile_pic || "",
        });
      } catch (error) {
        console.error("Erreur lors de la récupération du profil :", error);
        if (error.response?.status === 401) {
          setUser(null);
          localStorage.removeItem("user");
          navigate("/login");
        }
      } finally {
        setIsLoaded(true);
      }
    };
    fetchData();
  }, [user, navigate, setUser]);

  // Initialisation d'AOS
  useEffect(() => {
    AOS.init({ duration: 1200, once: true });
    return () => {
      AOS.refresh();
    };
  }, []);

  useEffect(() => {
    if (isLoaded && profileData) {
      AOS.refresh();
    }
  }, [isLoaded, profileData]);

  // Fonction de déconnexion
  const handleLogout = () => {
    localStorage.removeItem("user");
    setUser(null);
    navigate("/login");
  };

  // Fonction de suppression du compte
  const handleDeleteAccount = async () => {
    if (
      window.confirm(
        "Êtes-vous sûr de vouloir supprimer votre compte ? Cette action est irréversible."
      )
    ) {
      try {
        await axios.delete(`${API_BASE_URL}/users/${user.id}`, {
          headers: { Authorization: `Bearer ${user.access_token}` },
        });
        localStorage.removeItem("user");
        setUser(null);
        navigate("/login");
      } catch (error) {
        console.error("Erreur lors de la suppression du compte :", error);
        alert("Une erreur s'est produite lors de la suppression du compte.");
      }
    }
  };

  // Activer le mode édition
  const handleEdit = () => {
    setEditMode(true);
  };

  // Annuler l'édition
  const handleCancelEdit = () => {
    setEditMode(false);
    setEditedData({
      name: profileData.name || "",
      profile_pic: profileData.profile_pic || "",
    });
  };

  // Enregistrer les modifications
  const handleSaveEdit = async () => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/users/${user.id}`,
        editedData,
        { headers: { Authorization: `Bearer ${user.access_token}` } }
      );
      setProfileData(response.data);
      // Mettre à jour l'utilisateur localement (si nécessaire)
      const updatedUser = {
        ...user,
        name: response.data.name,
        profile_pic: response.data.profile_pic,
      };
      setUser(updatedUser);
      setEditMode(false);
    } catch (error) {
      console.error("Erreur lors de la mise à jour du profil :", error);
      alert("Une erreur s'est produite lors de la mise à jour du profil.");
    }
  };

  // Gestion des champs de saisie en mode édition
  const handleChange = (e) => {
    setEditedData({ ...editedData, [e.target.name]: e.target.value });
  };

  if (!isLoaded) {
    return <div style={loadingStyle}>Chargement...</div>;
  }

  if (isLoaded && !profileData) {
    return <div style={loadingStyle}>Erreur de chargement des données</div>;
  }

  if (!isLoaded) {
    return <div style={loadingStyle}>Loading...</div>;
  }

  if (isLoaded && !profileData) {
    return <div style={loadingStyle}>Error loading data</div>;
  }

  return (
    <div style={pageStyle}>
      {/* Modern Navigation */}
      <nav style={navStyle}>
        <div style={navContainerStyle}>
          <span style={navLogoStyle} onClick={() => navigate("/")} role="button">
            Dansy
          </span>
          
          <div style={navButtonsStyle}>
            <button onClick={() => navigate("/sessions")} style={sessionButton}>
              <span style={{ marginRight: '8px' }}>🎬</span>
              My Sessions
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div style={heroStyle}>
        <div style={heroContentStyle}>
          <h1 style={heroTitleStyle} data-aos="fade-down">My Profile</h1>
          <p style={heroSubtitleStyle} data-aos="fade-up">
            Manage your account settings
          </p>
        </div>
      </div>

      {/* Content */}
      <div style={contentContainerStyle}>
        <div style={contentWrapperStyle} data-aos="fade-up">
          {/* Profile Image */}
          <div style={profileHeaderStyle}>
            <div style={profileImageContainerStyle}>
              <img
                src={profileData.profile_pic?.replace("=s96-c", "=s200-c")}
                alt="Profile"
                style={profileImageStyle}
              />
            </div>
            {!editMode && (
              <button onClick={handleEdit} style={editHeaderButtonStyle}>
                ✏️ Edit Profile
              </button>
            )}
          </div>

          {/* Profile Info */}
          <div style={infoContainerStyle}>
            {editMode ? (
              <>
                <div style={infoGroupStyle}>
                  <label style={labelStyle}>Name</label>
                  <input
                    type="text"
                    name="name"
                    value={editedData.name}
                    onChange={handleChange}
                    style={inputEditStyle}
                  />
                </div>
                <div style={infoGroupStyle}>
                  <label style={labelStyle}>Profile Picture URL</label>
                  <input
                    type="text"
                    name="profile_pic"
                    value={editedData.profile_pic}
                    onChange={handleChange}
                    style={inputEditStyle}
                  />
                </div>
              </>
            ) : (
              <>
                <div style={infoGroupStyle}>
                  <label style={labelStyle}>Name</label>
                  <div style={valueStyle}>{profileData.name || 'Not available'}</div>
                </div>
                <div style={infoGroupStyle}>
                  <label style={labelStyle}>Email</label>
                  <div style={valueStyle}>{profileData.email || 'Not available'}</div>
                </div>
                <div style={infoGroupStyle}>
                  <label style={labelStyle}>Member Since</label>
                  <div style={valueStyle}>
                    {profileData.created_at
                      ? new Date(profileData.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })
                      : 'Not available'}
                  </div>
                </div>
                <div style={infoGroupStyle}>
                  <label style={labelStyle}>Last Login</label>
                  <div style={valueStyle}>
                    {profileData.last_login
                      ? new Date(profileData.last_login).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                      : 'Not available'}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Action Buttons */}
          {editMode ? (
            <div style={buttonContainerStyle}>
              <button onClick={handleSaveEdit} style={saveButtonStyle}>
                💾 Save Changes
              </button>
              <button onClick={handleCancelEdit} style={cancelButtonStyle}>
                Cancel
              </button>
            </div>
          ) : (
            <div style={buttonContainerStyle}>
              <button onClick={handleLogout} style={logoutButtonStyle}>
                🚪 Logout
              </button>
              <button onClick={handleDeleteAccount} style={deleteAccountButtonStyle}>
                🗑️ Delete Account
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* === Styles === */
const pageStyle = {
  minHeight: "100vh",
  backgroundColor: "#fafafa",
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif",
};

const navStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  backgroundColor: "rgba(255, 255, 255, 0.98)",
  borderBottom: "1px solid #f0f0f0",
  backdropFilter: "blur(10px)",
  zIndex: 1000,
  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
};

const navContainerStyle = {
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "16px 24px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const navLogoStyle = {
  fontSize: '1.5rem',
  fontFamily: "'Shirkhand', cursive",
  color: '#ff477e',
  fontWeight: 'bold',
  cursor: 'pointer',
  transition: 'transform 0.2s ease',
};

const navButtonsStyle = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
};

const sessionButton = {
  padding: "10px 20px",
  border: "1px solid #e0e0e0",
  borderRadius: "24px",
  backgroundColor: "white",
  color: "#333",
  fontSize: "15px",
  fontWeight: "500",
  cursor: "pointer",
  transition: "all 0.3s ease",
  display: "flex",
  alignItems: "center",
};

const heroStyle = {
  paddingTop: "120px",
  paddingBottom: "60px",
  background: "linear-gradient(135deg, #ff477e 0%, #ff6b9d 100%)",
  textAlign: "center",
  color: "white",
};

const heroContentStyle = {
  maxWidth: "800px",
  margin: "0 auto",
  padding: "0 24px",
};

const heroTitleStyle = {
  fontSize: "48px",
  fontWeight: "700",
  marginBottom: "16px",
  letterSpacing: "-0.02em",
};

const heroSubtitleStyle = {
  fontSize: "20px",
  opacity: 0.95,
  fontWeight: "400",
};

const contentContainerStyle = {
  maxWidth: "800px",
  margin: "-40px auto 0",
  padding: "0 24px 60px",
  position: "relative",
};

const contentWrapperStyle = {
  backgroundColor: "white",
  borderRadius: "24px",
  padding: "40px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
};

const profileHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "40px",
  paddingBottom: "24px",
  borderBottom: "1px solid #f0f0f0",
};

const profileImageContainerStyle = {
  display: "flex",
  alignItems: "center",
  gap: "20px",
};

const profileImageStyle = {
  width: "100px",
  height: "100px",
  borderRadius: "50%",
  border: "4px solid #f0f0f0",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
};

const editHeaderButtonStyle = {
  padding: "10px 20px",
  border: "none",
  borderRadius: "12px",
  background: "linear-gradient(135deg, #ff477e, #ff6b9d)",
  color: "white",
  fontSize: "15px",
  fontWeight: "500",
  cursor: "pointer",
  transition: "all 0.3s ease",
  boxShadow: "0 4px 12px rgba(255, 71, 126, 0.2)",
};

const infoContainerStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  marginBottom: "32px",
};

const infoGroupStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
};

const labelStyle = {
  fontSize: "14px",
  fontWeight: "600",
  color: "#999",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const valueStyle = {
  fontSize: "18px",
  color: "#333",
  fontWeight: "500",
};

const inputEditStyle = {
  width: "100%",
  padding: "12px 16px",
  fontSize: "16px",
  border: "2px solid #e0e0e0",
  borderRadius: "12px",
  outline: "none",
  transition: "border-color 0.3s ease",
  backgroundColor: "#fafafa",
};

const buttonContainerStyle = {
  display: "flex",
  gap: "12px",
  justifyContent: "flex-end",
  paddingTop: "24px",
  borderTop: "1px solid #f0f0f0",
};

const saveButtonStyle = {
  padding: "12px 24px",
  border: "none",
  borderRadius: "12px",
  background: "linear-gradient(135deg, #ff477e, #ff6b9d)",
  color: "white",
  fontSize: "15px",
  fontWeight: "500",
  cursor: "pointer",
  transition: "all 0.3s ease",
  boxShadow: "0 4px 12px rgba(255, 71, 126, 0.2)",
};

const cancelButtonStyle = {
  padding: "12px 24px",
  border: "1px solid #e0e0e0",
  borderRadius: "12px",
  backgroundColor: "white",
  color: "#666",
  fontSize: "15px",
  fontWeight: "500",
  cursor: "pointer",
  transition: "all 0.3s ease",
};

const logoutButtonStyle = {
  padding: "12px 24px",
  border: "1px solid #e0e0e0",
  borderRadius: "12px",
  backgroundColor: "white",
  color: "#666",
  fontSize: "15px",
  fontWeight: "500",
  cursor: "pointer",
  transition: "all 0.3s ease",
};

const deleteAccountButtonStyle = {
  padding: "12px 24px",
  border: "1px solid #ff4d4d",
  borderRadius: "12px",
  backgroundColor: "white",
  color: "#ff4d4d",
  fontSize: "15px",
  fontWeight: "500",
  cursor: "pointer",
  transition: "all 0.3s ease",
};

const loadingStyle = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "100vh",
  fontSize: "18px",
  color: "#666",
};
